import { useCallback, useEffect, useMemo, useState } from 'react'

import { useQuery } from 'react-query'

import { getSubscriptionPlans } from 'services/endpoints/settings/general/subscription'
import type { PaymentPlan } from 'types/api/subscription'

type SubscriptionPlansResponse = {
  subscriptions: PaymentPlan[]
  // campaign intentionally ignored
}

const getPrice = (data?: SubscriptionPlansResponse) => {
  const subs = data?.subscriptions ?? []
  if (!subs.length) return null

  let min = subs[0].price
  subs.forEach((plan, index) => {
    const value = subs[index].price
    min = value < min ? value : min
  })
  return min
}

export function useSubscriptionPlans({
  otherUserId,
  shouldLoadSubscriptionPlans,
}: {
  otherUserId?: string
  shouldLoadSubscriptionPlans: boolean
}) {
  const [selectedSubscriptionPlan, setSelectedSubscriptionPLan] =
  useState<string | null>(null)
  const [price, setPrice] = useState<number | null>(null)

const handleSetSelectedSubscriptionPlan = useCallback(
  (planId: string) => {
    setSelectedSubscriptionPLan(planId)
  },
  []
)

  const queryKey = useMemo(
    () => ['allSubsPlans_' + otherUserId, otherUserId] as const,
    [otherUserId]
  )

  const { data: subscriptionPlans, isLoading, error } = useQuery(
    queryKey,
    async () => {
      if (!otherUserId) {
        throw new Error('Missing otherUserId')
      }

      return await getSubscriptionPlans(otherUserId)
    },
    {
      enabled: shouldLoadSubscriptionPlans && !!otherUserId,
      staleTime: 20_000,
      onSuccess: resp => {
        if (resp?.subscriptions?.length) {
          const defaultPlan = resp.subscriptions.find(
            (plan: PaymentPlan) => plan.month_count === 1
          )?.id

          if (defaultPlan) {
            handleSetSelectedSubscriptionPlan(defaultPlan)
          }
        }
      }
    }
  )

  useEffect(() => {
    if (shouldLoadSubscriptionPlans && subscriptionPlans?.subscriptions?.length) {
      const minPrice = getPrice(subscriptionPlans)
      setPrice(minPrice)
    }
  }, [shouldLoadSubscriptionPlans, subscriptionPlans])

  return {
    // keep naming identical to your component usage
    subscriptionPlans,
    isLoading,
    error,

    selectedSubscriptionPlan,
    setSelectedSubscriptionPLan,
    handleSetSelectedSubscriptionPlan,

    price,

    queryKey,
  }
}